/**
 * absence-requests.ts
 * Shared wording of the worker's own absence requests, used by the
 * Solicitudes tab (lib/my-requests.ts, SolicitudesView.vue) and Inicio's "Tus solicitudes"
 * card (HomeView.vue) — operator-app-unificada T5.
 *
 * Type names come from GET /rrhh/self/absence-types, which only answers a
 * user who may file a request (self.ausencia.solicitar): callers ask only
 * then. Without the names a request reads "Ausencia".
 */
import type { BadgeVariant } from '@suite/ui';
import type { SelfCalendarEventResponse } from '@suite/contracts-rrhh';
import { getMyAbsenceTypes } from './api.js';

export const FALLBACK_ABSENCE_TYPE_NAME = 'Ausencia';

type AbsenceState = SelfCalendarEventResponse['currentState'];

export const ABSENCE_STATE_VARIANTS: Record<AbsenceState, BadgeVariant> = {
  SOLICITADA: 'warning',
  APROBADA: 'success',
  RECHAZADA: 'danger',
  CANCELADA: 'neutral',
};

export const ABSENCE_STATE_LABELS: Record<AbsenceState, string> = {
  SOLICITADA: 'Esperando aprobación',
  APROBADA: 'Aprobada',
  RECHAZADA: 'Rechazada',
  CANCELADA: 'Cancelada',
};

/** typeId → name; an empty map when the names could not be loaded (they are a nicety). */
export async function fetchAbsenceTypeNames(): Promise<Map<string, string>> {
  try {
    const { items } = await getMyAbsenceTypes();
    return new Map(items.map((t) => [t.id, t.name]));
  } catch {
    return new Map();
  }
}

export function absenceTypeName(names: ReadonlyMap<string, string>, absence: Pick<SelfCalendarEventResponse, 'typeId'>): string {
  return names.get(absence.typeId) ?? FALLBACK_ABSENCE_TYPE_NAME;
}

function utcDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

/** "9 nov" */
function shortDate(iso: string): string {
  return utcDate(iso)
    .toLocaleDateString('es-AR', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .replace('.', '');
}

/** "9 nov", or "27 oct – 10 nov" (both ends included). */
export function absenceDateRange(absence: Pick<SelfCalendarEventResponse, 'startDate' | 'endDate'>): string {
  return absence.startDate === absence.endDate ? shortDate(absence.startDate) : `${shortDate(absence.startDate)} – ${shortDate(absence.endDate)}`;
}

/** Days the absence covers, both ends included. */
export function absenceDayCount(absence: Pick<SelfCalendarEventResponse, 'startDate' | 'endDate'>): number {
  return Math.round((utcDate(absence.endDate).getTime() - utcDate(absence.startDate).getTime()) / 86_400_000) + 1;
}
